/**
 * OfflineDatabase - IndexedDB schema using Dexie.js
 *
 * Manages offline storage for encrypted book content, licenses, and sync queue.
 * Uses Dexie.js for easier IndexedDB management with TypeScript support.
 *
 * Tables:
 * - books: Book metadata and encryption info
 * - epubChunks: Encrypted EPUB chapter content (with downloadedAt for DRM tracking)
 * - pdfPages: Encrypted PDF page ranges (with downloadedAt for DRM tracking)
 * - licenses: User licenses for offline books
 * - drmQuotaCache: Cached DRM quota information per book/user
 * - syncQueue: Pending sync operations (progress, highlights, notes)
 * - readingState: Current reading state per book
 * - downloadProgress: Active download sessions
 */

import Dexie from 'dexie';
import type { Table } from 'dexie';

export interface TranslationCacheEntry {
  language: string;
  translations: Record<string, string>;
  timestamp: number;
}
import type {
  OfflineBook,
  EpubChunk,
  PdfPage,
  ChapterResource,
  OfflineLicense,
  SyncOperation,
  OfflineReadingState,
  DownloadProgress,
  DrmQuotaCache,
  DownloadedChapterInfo,
} from '@/types/offline';

export class OfflineDatabase extends Dexie {
  // Table declarations
  books!: Table<OfflineBook, string>;
  epubChunks!: Table<EpubChunk, string>;
  chapterResources!: Table<ChapterResource, string>;
  pdfPages!: Table<PdfPage, string>;
  licenses!: Table<OfflineLicense, string>;
  drmQuotaCache!: Table<DrmQuotaCache, string>;
  syncQueue!: Table<SyncOperation, string>;
  readingState!: Table<OfflineReadingState, string>;
  downloadProgress!: Table<DownloadProgress, string>;
  translations!: Table<TranslationCacheEntry, string>;

  constructor() {
    super('learnleaf_offline');

    // Define schema
    this.version(1).stores({
      // Books: id is primary key, indexed by format and downloadedAt
      books: 'id, format, downloadedAt, lastAccessed',

      // EPUB Chunks: composite index on bookId + chapterIndex for efficient queries
      // Includes downloadedAt for DRM quota tracking
      epubChunks: 'id, bookId, [bookId+chapterIndex], chapterIndex, downloadedAt',

      // Chapter Resources: images, CSS, fonts embedded in chapter HTML
      // Indexed by bookId + chapterIndex for efficient chapter resource queries
      chapterResources: 'id, bookId, [bookId+chapterIndex], chapterIndex, originalUrl, resourceType, downloadedAt',

      // PDF Pages: indexed by bookId for efficient queries
      // Includes downloadedAt for DRM quota tracking
      pdfPages: 'id, bookId, downloadedAt',

      // Licenses: indexed by bookId and userId
      licenses: 'id, bookId, userId, [bookId+userId]',

      // DRM Quota Cache: indexed by bookId and userId
      drmQuotaCache: 'id, bookId, userId, [bookId+userId]',

      // Sync Queue: indexed by operation type and creation date
      syncQueue: 'id, bookId, type, createdAt, [type+createdAt]',

      // Reading State: bookId is primary key, indexed by lastRead
      readingState: 'bookId, lastRead',

      // Download Progress: bookId is primary key, indexed by status
      downloadProgress: 'bookId, status',
    });

    this.version(2).stores({
      translations: 'language',
    });

    // v3: OfflineLicense now includes licenseType, status, expirationDate
    // No index changes needed — expiry is checked in application code
    this.version(3).stores({});
  }

  /**
   * Get all offline books
   */
  async getAllBooks(): Promise<OfflineBook[]> {
    return this.books.toArray();
  }

  /**
   * Get book by ID
   */
  async getBook(bookId: string): Promise<OfflineBook | undefined> {
    return this.books.get(bookId);
  }

  /**
   * Check if book is available offline
   */
  async isBookOffline(bookId: string): Promise<boolean> {
    const book = await this.getBook(bookId);
    return book !== undefined;
  }

  /**
   * Store book metadata
   */
  async storeBook(book: OfflineBook): Promise<void> {
    await this.books.put(book);
  }

  /**
   * Delete book and all related content
   */
  async deleteBook(bookId: string): Promise<void> {
    await this.transaction('rw', [
      this.books,
      this.epubChunks,
      this.chapterResources,
      this.pdfPages,
      this.licenses,
      this.drmQuotaCache,
      this.readingState,
    ], async () => {
      await this.books.delete(bookId);
      await this.epubChunks.where('bookId').equals(bookId).delete();
      await this.chapterResources.where('bookId').equals(bookId).delete();
      await this.pdfPages.where('bookId').equals(bookId).delete();
      await this.licenses.where('bookId').equals(bookId).delete();
      await this.drmQuotaCache.where('bookId').equals(bookId).delete();
      await this.readingState.delete(bookId);
    });
  }

  /**
   * Get all EPUB chunks for a book
   */
  async getEpubChunks(bookId: string): Promise<EpubChunk[]> {
    return this.epubChunks
      .where('[bookId+chapterIndex]')
      .between([bookId, 0], [bookId, Number.MAX_SAFE_INTEGER])
      .sortBy('chapterIndex');
  }

  /**
   * Get specific EPUB chunk
   */
  async getEpubChunk(bookId: string, chapterIndex: number): Promise<EpubChunk | undefined> {
    return this.epubChunks
      .where('[bookId+chapterIndex]')
      .equals([bookId, chapterIndex])
      .first();
  }

  /**
   * Store EPUB chunk
   */
  async storeEpubChunk(chunk: EpubChunk): Promise<void> {
    await this.epubChunks.put(chunk);
  }

  /**
   * Store multiple EPUB chunks (batch operation)
   */
  async storeEpubChunks(chunks: EpubChunk[]): Promise<void> {
    await this.epubChunks.bulkPut(chunks);
  }

  /**
   * Get chapter resources for a specific chapter
   */
  async getChapterResources(bookId: string, chapterIndex: number): Promise<ChapterResource[]> {
    return this.chapterResources
      .where('[bookId+chapterIndex]')
      .equals([bookId, chapterIndex])
      .toArray();
  }

  /**
   * Store chapter resource
   */
  async storeChapterResource(resource: ChapterResource): Promise<void> {
    await this.chapterResources.put(resource);
  }

  /**
   * Store multiple chapter resources (batch operation)
   */
  async storeChapterResources(resources: ChapterResource[]): Promise<void> {
    await this.chapterResources.bulkPut(resources);
  }

  /**
   * Delete all resources for a chapter
   */
  async deleteChapterResources(bookId: string, chapterIndex: number): Promise<void> {
    await this.chapterResources
      .where('[bookId+chapterIndex]')
      .equals([bookId, chapterIndex])
      .delete();
  }

  /**
   * Get all PDF pages for a book
   */
  async getPdfPages(bookId: string): Promise<PdfPage[]> {
    return this.pdfPages.where('bookId').equals(bookId).toArray();
  }

  /**
   * Store PDF page
   */
  async storePdfPage(page: PdfPage): Promise<void> {
    await this.pdfPages.put(page);
  }

  /**
   * Store multiple PDF pages (batch operation)
   */
  async storePdfPages(pages: PdfPage[]): Promise<void> {
    await this.pdfPages.bulkPut(pages);
  }

  /**
   * Get license for book
   */
  async getLicense(bookId: string, userId: string): Promise<OfflineLicense | undefined> {
    return this.licenses
      .where('[bookId+userId]')
      .equals([bookId, userId])
      .first();
  }

  /**
   * Store license
   */
  async storeLicense(license: OfflineLicense): Promise<void> {
    await this.licenses.put(license);
  }

  /**
   * Get reading state for book
   */
  async getReadingState(bookId: string): Promise<OfflineReadingState | undefined> {
    return this.readingState.get(bookId);
  }

  /**
   * Update reading state
   */
  async updateReadingState(state: OfflineReadingState): Promise<void> {
    await this.readingState.put(state);
  }

  /**
   * Add operation to sync queue
   */
  async addToSyncQueue(operation: SyncOperation): Promise<void> {
    await this.syncQueue.add(operation);
  }

  /**
   * Get all pending sync operations
   */
  async getSyncQueue(): Promise<SyncOperation[]> {
    return this.syncQueue.orderBy('createdAt').toArray();
  }

  /**
   * Remove operation from sync queue
   */
  async removeFromSyncQueue(operationId: string): Promise<void> {
    await this.syncQueue.delete(operationId);
  }

  /**
   * Clear sync queue for a book
   */
  async clearSyncQueueForBook(bookId: string): Promise<void> {
    await this.syncQueue.where('bookId').equals(bookId).delete();
  }

  /**
   * Get download progress
   */
  async getDownloadProgress(bookId: string): Promise<DownloadProgress | undefined> {
    return this.downloadProgress.get(bookId);
  }

  /**
   * Update download progress
   */
  async updateDownloadProgress(progress: DownloadProgress): Promise<void> {
    await this.downloadProgress.put(progress);
  }

  /**
   * Delete download progress
   */
  async deleteDownloadProgress(bookId: string): Promise<void> {
    await this.downloadProgress.delete(bookId);
  }

  /**
   * Get total storage used by offline content
   */
  async getTotalStorageUsed(): Promise<number> {
    const books = await this.books.toArray();
    return books.reduce((total, book) => total + book.sizeBytes, 0);
  }

  /**
   * Get books sorted by last accessed (for cleanup)
   */
  async getBooksOldestFirst(): Promise<OfflineBook[]> {
    return this.books.orderBy('lastAccessed').toArray();
  }

  /**
   * Update last accessed timestamp for a book
   */
  async updateLastAccessed(bookId: string): Promise<void> {
    const book = await this.books.get(bookId);
    if (book) {
      book.lastAccessed = new Date();
      await this.books.put(book);
    }
  }

  /**
   * Store DRM quota cache
   */
  async storeDrmQuota(quota: DrmQuotaCache): Promise<void> {
    await this.drmQuotaCache.put(quota);
  }

  /**
   * Get DRM quota cache for a book/user
   */
  async getDrmQuota(bookId: string, userId: string): Promise<DrmQuotaCache | undefined> {
    return this.drmQuotaCache
      .where('[bookId+userId]')
      .equals([bookId, userId])
      .first();
  }

  /**
   * Get downloaded chapters for a book
   */
  async getDownloadedChapters(bookId: string): Promise<DownloadedChapterInfo[]> {
    const chunks = await this.epubChunks
      .where('bookId')
      .equals(bookId)
      .toArray();

    return chunks.map(chunk => ({
      bookId: chunk.bookId,
      chapterIndex: chunk.chapterIndex,
      downloadedAt: chunk.downloadedAt,
      isAvailableOffline: true,
    }));
  }

  /**
   * Check if specific chapter is downloaded
   */
  async isChapterDownloaded(bookId: string, chapterIndex: number): Promise<boolean> {
    const chunk = await this.epubChunks
      .where('[bookId+chapterIndex]')
      .equals([bookId, chapterIndex])
      .first();

    return chunk !== undefined;
  }

  /**
   * Get count of downloaded chapters for a book
   */
  async getDownloadedChapterCount(bookId: string): Promise<number> {
    return this.epubChunks.where('bookId').equals(bookId).count();
  }

  /**
   * Clear all offline data (for testing or user logout)
   */
  async clearAllData(): Promise<void> {
    await this.transaction('rw', [
      this.books,
      this.epubChunks,
      this.pdfPages,
      this.licenses,
      this.drmQuotaCache,
      this.syncQueue,
      this.readingState,
      this.downloadProgress,
    ], async () => {
      await this.books.clear();
      await this.epubChunks.clear();
      await this.pdfPages.clear();
      await this.licenses.clear();
      await this.drmQuotaCache.clear();
      await this.syncQueue.clear();
      await this.readingState.clear();
      await this.downloadProgress.clear();
    });
  }

  /**
   * Get database statistics
   */
  async getStats(): Promise<{
    bookCount: number;
    epubChunkCount: number;
    pdfPageCount: number;
    syncQueueLength: number;
    totalStorageBytes: number;
  }> {
    const [bookCount, epubChunkCount, pdfPageCount, syncQueueLength, totalStorageBytes] = await Promise.all([
      this.books.count(),
      this.epubChunks.count(),
      this.pdfPages.count(),
      this.syncQueue.count(),
      this.getTotalStorageUsed(),
    ]);

    return {
      bookCount,
      epubChunkCount,
      pdfPageCount,
      syncQueueLength,
      totalStorageBytes,
    };
  }

  async getTranslations(language: string): Promise<TranslationCacheEntry | undefined> {
    return this.translations.get(language);
  }

  async setTranslations(language: string, translations: Record<string, string>): Promise<void> {
    await this.translations.put({
      language,
      translations,
      timestamp: Date.now(),
    });
  }

  async clearTranslations(): Promise<void> {
    await this.translations.clear();
  }
}

// Singleton instance
export const offlineDatabase = new OfflineDatabase();

// Alias for i18n module
export const offlineDb = offlineDatabase;
